import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { flushAllBlobSyncs } from '../lib/syncBlob';
import { honourContentReset } from '../lib/contentReset';
import { forgetSignedOutAccount } from '../lib/signOutReset';
import { reportAccount } from '../lib/accountStorage';
import { landOnHome } from '../hooks/useCurrentView';

/** How long a sign-out waits for pending edits to be sent. */
const SIGN_OUT_FLUSH_MS = 2500;

interface AuthState {
  user: { id: string; email: string } | null;
  loading: boolean;
  error: string | null;
  /** Set after a signup that requires email confirmation (no session yet). */
  pendingConfirmationEmail: string | null;
  /** `captchaToken`: Cloudflare Turnstile — required once Supabase's CAPTCHA protection is on. */
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>;
  signUp: (email: string, password: string, username: string, captchaToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
  resendConfirmation: (email: string, captchaToken?: string) => Promise<void>;
  clearPendingConfirmation: () => void;
}

const defaultState = {
  user: null,
  loading: true,
  error: null,
  pendingConfirmationEmail: null
};

export const useAuthStore = create<AuthState>()(
  persist<AuthState, [], [], any>(
    (set) => ({
      ...defaultState,

      signUp: async (email, password, username, captchaToken) => {
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Sign up request timed out. Please check your connection.')), 15000)
        );

        try {
          set({ loading: true, error: null });

          if (!email || !password || !username.trim()) {
            throw new Error('All fields are required');
          }

          const signUpRequest = supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                username: username.trim()
              },
              emailRedirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
              captchaToken,
            }
          });

          const { data, error } = await Promise.race([signUpRequest, timeout]) as any;

          if (error) throw error;

          if (data.session && data.user) {
            // Email confirmation is disabled — a session was returned, so the
            // user is logged in immediately.
            const user = {
              id: data.user.id,
              email: data.user.email!,
            };

            set({ user, loading: false, pendingConfirmationEmail: null });
            syncManager.setUserId(user.id);
          } else {
            // Email confirmation is required — no session yet. The public.users
            // and user_profiles rows are created server-side by the
            // handle_new_user trigger, so there is nothing to insert here. The
            // UI shows a "check your email" screen until the user confirms.
            set({ loading: false, pendingConfirmationEmail: email });
          }
        } catch (error: any) {
          const errorMessage = error.message || 'Sign up failed. Please try again.';
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      signIn: async (email, password, captchaToken) => {
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Sign in request timed out. Please check your connection.')), 15000)
        );

        try {
          set({ loading: true, error: null });

          if (!email || !password) {
            throw new Error('Email and password are required');
          }

          const signInRequest = supabase.auth.signInWithPassword({
            email,
            password,
            options: { captchaToken },
          });

          const { data, error } = await Promise.race([signInRequest, timeout]) as any;

          if (error) throw error;

          if (data.user) {
            const user = {
              id: data.user.id,
              email: data.user.email!,
            };

            /* A sign-in opens on Home, not on the page the last session ended on
               (owner, 2026-10-05). BEFORE `set({ user })`, so the first signed-in
               paint is already Home. */
            landOnHome();
            set({ user, loading: false });
            syncManager.setUserId(user.id);

            await supabase.from('users')
              .update({ last_login: new Date().toISOString() })
              .eq('id', data.user.id)
              .then(() => {}, () => {});
          }
        } catch (error: any) {
          const errorMessage = error.message || 'Sign in failed. Please try again.';
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      signOut: async () => {
        /* ⚠️ Send this account's pending edits while it is still signed in. A change
           waiting for its debounced write would otherwise go out with no session —
           skipped — and wait on this device for the account's next change there.
           Bounded: a slow network must not hold the sign-out. (A flush never rejects:
           a failed write stays on this device.) */
        await Promise.race([flushAllBlobSyncs(), new Promise(r => setTimeout(r, SIGN_OUT_FLUSH_MS))]);
        try {
          await supabase.auth.signOut();
          set({ user: null, error: null });
          syncManager.setUserId(null);
        } catch (error: any) {
          console.error('Sign out error:', error);
          set({ user: null, error: null });
        }
        landOnHome();
      },

      checkAuth: async () => {
        initAuthListener();

        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Authentication check timed out')), 8000)
        );

        try {
          set({ loading: true });

          const authCheck = supabase.auth.getSession();
          const result = await Promise.race([authCheck, timeout]) as any;
          const { data: { session } = { session: null }, error } = result || {};

          if (error) {
            console.warn('Auth check error, continuing without authentication:', error);
            set({ user: null, loading: false });
            return;
          }

          if (session?.user) {
            /* ⚠️⚠️ **BEFORE `set({ user })`, AND THAT IS THE WHOLE POINT.** Every
               store's initialize() is gated on `user`, and a local-first store
               that boots from its cache upserts that cache straight back to the
               cloud — which is exactly how a reset account restored its own
               growth journey. Once this resolves to true the page is reloading,
               so there is nothing left to set.
               ⚠️ It fails open: see `honourContentReset`. */
            if (await honourContentReset(session.user.id)) return;

            const user = {
              id: session.user.id,
              email: session.user.email!,
            };
            set({ user, loading: false });
            syncManager.setUserId(user.id);

            syncManager.initialize().catch(err => {
              console.warn('Sync initialization failed, continuing offline:', err);
            });
          } else {
            set({ user: null, loading: false });
          }
        } catch (error: any) {
          // Transient failure/timeout (slow or offline network). Do NOT force a
          // sign-out here: if a session was rehydrated from storage, keep the
          // user logged in so we don't flash the landing page on a flaky
          // connection. onAuthStateChange corrects the state (incl. a real
          // SIGNED_OUT) once connectivity returns.
          console.warn('Auth check failed/timed out; keeping any cached session and continuing offline:', error);
          set({ loading: false, error: null });
        }
      },

      resendConfirmation: async (email, captchaToken) => {
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email,
          options: {
            emailRedirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
            captchaToken,
          }
        });
        if (error) throw error;
      },

      clearPendingConfirmation: () => set({ pendingConfirmationEmail: null, error: null }),
    }),
    {
      name: 'auth-storage',
      version: 2,
      migrate: (persistedState: any, version: number) => {
        try {
          if (version < 2 || !persistedState || typeof persistedState !== 'object') {
            return defaultState as unknown as AuthState;
          }
          return {
            ...defaultState,
            user: persistedState.user || null,
            error: null
          } as unknown as AuthState;
        } catch (error) {
          console.warn('Auth state migration failed, using defaults:', error);
          return defaultState as unknown as AuthState;
        }
      },
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.warn('Auth state rehydration failed:', error);
          useAuthStore.setState(defaultState);
        }
      },
      partialize: (state) => ({
        user: state.user
      })
    }
  )
);

/* ⚠️⚠️ ON THE VALUE, SO EVERY ROUTE OUT IS CAUGHT. `user` leaves through `signOut()`,
   the SIGNED_OUT event (another tab, an expired session, account deletion) and
   `checkAuth` finding no session — or becomes ANOTHER account when one signs in
   elsewhere on this device. Watching the id catches all of them, and any route added
   later. A same-account update (token refresh, the second report of a sign-in) and a
   sign-in from signed out forget nothing. (lib/signOutReset) */
useAuthStore.subscribe((state, prev) => {
  const was = prev.user?.id;
  if (was && was !== state.user?.id) forgetSignedOutAccount();
  reportAccount(state.user?.id ?? null);
});
/* ⭐ The persisted stores keep one copy per account (lib/accountStorage): whose copy to
   read is the user this store hydrated, then every change of it — the same value. */
reportAccount(useAuthStore.getState().user?.id ?? null);

// Dev-only: expose the auth store on window for debugging/inspection.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { authStore?: typeof useAuthStore }).authStore = useAuthStore;
}

// Keep the auth store in sync with Supabase auth events. This is what makes the
// post-email-confirmation redirect work: when the user clicks the confirmation
// link, detectSessionInUrl exchanges the token and fires SIGNED_IN, logging them
// in reactively without a manual refresh.
let authListenerInitialized = false;
export function initAuthListener() {
  if (authListenerInitialized) return;
  authListenerInitialized = true;

  supabase.auth.onAuthStateChange((event, session) => {
    if (
      (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') &&
      session?.user
    ) {
      const user = { id: session.user.id, email: session.user.email! };
      const current = useAuthStore.getState().user;
      if (!current || current.id !== user.id) {
        useAuthStore.setState({ user, loading: false, pendingConfirmationEmail: null });
        syncManager.setUserId(user.id);
      }
    } else if (event === 'SIGNED_OUT') {
      /* Every other way out (another tab, account deletion, an expired session)
         arrives here, not through `signOut()`. */
      landOnHome();
      useAuthStore.setState({ user: null });
      syncManager.setUserId(null);
    }
  });
}